<template>
    <PhpEolLayout
        :supported-modules="supportedModules"
        :active-module="activeModule"
        @module-changed="changeModule"
    >
        <DCard padding>
            <DTable
                :items="props.items"
                :fields="fields"
            >
                <template #batch_label="{item}">
                    <td>
                        {{ item.batch_label }}
                    </td>
                </template>
                <template #created_at="{item}">
                    <td>
                        {{ item.created_at }}
                    </td>
                </template>
                <template #count="{item}">
                    <td>
                        {{ item.count }}
                    </td>
                </template>
                <template #file_name="{item}">
                    <td>
                        {{ item.batch.file_name }}
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <div>
                            <DDropdown
                                :id="`action_dropdown_${item.id}`"
                                icon="dots-horizontal"
                            >
                                <DDropdownItem
                                    :id="`first_email_action_${item.id}`"
                                    @click="$inertia.get($route('contracts.php-eol-first-email-candidates.index', { batch_id: item.batch_id, module: activeModule }))"
                                >
                                    {{ __('tools.php_eol.dropdown_table_actions.first_email_candidates') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    :id="`first_email_action_${item.id}`"
                                    @click="$inertia.get($route('contracts.php-eol-second-email-candidates.index', { batch_id: item.batch_id, module: activeModule }))"
                                >
                                    {{ __('tools.php_eol.dropdown_table_actions.second_email_candidates') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    :id="`addon_candidates_action_${item.id}`"
                                    @click="$inertia.get($route('contracts.php-eol-addon-candidates.index', { batch_id: item.batch_id, module: activeModule }))"
                                >
                                    {{ __('tools.php_eol.dropdown_table_actions.addon_candidates') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    :id="`php-eol-processed_${item.id}`"
                                    @click="$inertia.get($route('contracts.php-eol-processed.index', { batch_id: item.batch_id, module: activeModule }))"
                                >
                                    {{ __('tools.php_eol.dropdown_table_actions.processed_records') }}
                                </DDropdownItem>

                                <DDropdownItem
                                    :id="`php-eol-discarded_${item.id}`"
                                    @click="$inertia.get($route('contracts.php-eol-discarded.index', { batch_id: item.batch_id, module: activeModule }))"
                                >
                                    {{ __('tools.php_eol.dropdown_table_actions.discarded_records') }}
                                </DDropdownItem>
                            </DDropdown>
                        </div>
                    </td>
                </template>
            </DTable>
            <DPagination
                v-if="batches.last_page >= 1"
                :active-page="batches.current_page"
                :pages="batches.last_page"
                align="center"
                @update:activePage="changePage"
            />
        </DCard>
        <CreatePhpEolBatchModal
            v-if="showModal"
            is-confirm
            is-awaiting-validation
            is-medium
            :active-module="activeModule"
            :staged-php-versions="stagedPhpVersions"
            :show-modal="showModal"
            @close-modal="showModal = false"
            @form-submitted="showWebsocketModal = true"
        />
        <WebSocketLoadingModal
            :title="isJsonInDownloadingState ? __('tools.php_eol.json_download.broadcast.creation.notice') : __('tools.php_eol.batches.broadcast.creation.notice')"
            :show-modal="showWebsocketModal"
            :internal-support-link="internalSupportLink"
            :soft-timeout="400"
            @close="showWebsocketModal = false"
        />
        <SetExcludedProductsModal
            is-confirm
            is-awaiting-validation
            is-big
            :all-module-products="allModuleProducts"
            :excluded-module-products="excludedModuleProducts"
            :active-module="activeModule"
            :show-modal="showExcludedProductsModal"
            @close-modal="showExcludedProductsModal = false"
        />
        <SetExcludedSubscriptionDomainsModal
            is-confirm
            is-awaiting-validation
            is-big
            :active-module="activeModule"
            :excluded-subscription-domains="excludedSubscriptionDomains"
            :show-modal="showExcludedDomainsModal"
            @close-modal="showExcludedDomainsModal = false"
        />
    </PhpEolLayout>
    <DFabs>
        <DFab
            v-if="userPermissions.includes('contract.fulfillment')"
            id="btn-add-excluded-products"
            icon="plus"
            :label="__('tools.php_eol.batches.excluded_products_button')"
            @click="showExcludedProductsModal = true"
        />
        <DFab
            v-if="userPermissions.includes('contract.fulfillment')"
            id="btn-add-excluded-subscription-domains"
            icon="plus"
            :label="__('tools.php_eol.batches.excluded_subscription_domains_button')"
            @click="showExcludedDomainsModal = true"
        />
        <DFab
            v-if="userPermissions.includes('contract.fulfillment')"
            id="btn-download-json"
            icon="download"
            :label="__('tools.php_eol.batches.predownload_json_button')"
            @click="showConfirmationModal = true"
        />
        <DFab
            v-if="userPermissions.includes('contract.fulfillment')"
            id="btn-add-bulk"
            icon="plus"
            :label="__('tools.php_eol.batches.create_batch_button')"
            @click="showModal = true"
        />
    </DFabs>
    <DModal
        v-model:show="showConfirmationModal"
        :title="__('tools.php_eol.batches.confirmation_modal.title')"
        :message="__('tools.php_eol.batches.confirmation_modal.message')"
        is-confirm
        is-error
        @close="showConfirmationModal = false"
        @confirm="downloadJsonFile"
    />
</template>

<script setup>

import { router, usePage } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, ref } from 'vue'
import WebSocketLoadingModal from '~@/Components/Common/Modals/WebSocketLoadingModal'
import CreatePhpEolBatchModal from '~@/Pages/Contracts/PhpEol/CreatePhpEolBatchModal.vue'
import SetExcludedProductsModal from '~@/Pages/Contracts/PhpEol/SetExcludedProductsModal.vue'
import SetExcludedSubscriptionDomainsModal from '~@/Pages/Contracts/PhpEol/SetExcludedSubscriptionDomainsModal.vue'
import PhpEolLayout from '~@/Pages/Contracts/PhpEol/Layout.vue'
import { route } from '~@/Composables/route'

const props = defineProps({
    batches: {
        type: Object,
        default: () => {
            return {}
        }
    },
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    allModuleProducts: {
        type: Array,
        default () {
            return []
        }
    },
    excludedModuleProducts: {
        type: Array,
        default () {
            return []
        }
    },
    excludedSubscriptionDomains: {
        type: Array,
        default () {
            return []
        }
    },
    supportedModules: {
        type: Array,
        default () {
            return []
        }
    },
    activeModule: {
        type: String,
        default () {
            return ''
        }
    },
    stagedPhpVersions: {
        type: Array,
        default () {
            return []
        }
    }
})

const fields = [
    { key: 'batch_label', label: __('tools.php_eol.batches.table.batch_label') },
    { key: 'created_at', label: __('tools.php_eol.batches.table.created_at') },
    { key: 'count', label: __('tools.php_eol.batches.table.number_of_contracts') },
    { key: 'file_name', label: __('tools.php_eol.batches.table.associated_file') },
    { key: 'actions', label: '', sorter: false, filter: false },
]

const userPermissions = computed(() => usePage().props.auth.permissions)
const internalSupportLink = computed(() => usePage().props.tenantSettings.internal_support_url)
const showModal = ref(false)
const showExcludedProductsModal = ref(false)
const showExcludedDomainsModal = ref(false)
const isJsonInDownloadingState = ref(false)
const showConfirmationModal = ref(false)
const showWebsocketModal = ref(false)

const changePage = function (page) {
    const queryParameters = {}

    if (page > 1) {
        queryParameters.page = page
    }
    queryParameters.module = props.activeModule
    router.get(route('contracts.php-eol-bulk.index'), queryParameters)
}

const changeModule = function (moduleName) {
    router.get(route('contracts.php-eol-bulk.index'), { module: moduleName })
}

const downloadJsonFile = () => {
    isJsonInDownloadingState.value = true

    window.axios.get(route('contracts.php-eol-bulk.download-json', { module: props.activeModule }))
        .then(function (response) {
            showWebsocketModal.value = true
            usePage().props.flash.message = response.data.message
        })
        .catch((error) => {
            usePage().props.flash.errorMessages = error.response.data.errorMessage
        })
}

const bulkCreationDone = function (message) {
    showWebsocketModal.value = false

    usePage().props.flash.message = message
    setTimeout(() => { changePage(1) }, 2000)
}

const bulkCreationFailed = function (error) {
    showWebsocketModal.value = false

    usePage().props.flash.message = null
    usePage().props.flash.errorMessages = error
}

const jsonDownloaded = function (message, closeModal) {
    if (closeModal === true) {
        showWebsocketModal.value = false
        isJsonInDownloadingState.value = false
    }

    usePage().props.flash.message = message
}

const jsonDownloadFailed = function (error) {
    showWebsocketModal.value = false
    isJsonInDownloadingState.value = false

    usePage().props.flash.message = null
    usePage().props.flash.errorMessages = error
}

const showEstimatedDownloadTime = function (message) {
    showWebsocketModal.value = true
    isJsonInDownloadingState.value = true

    usePage().props.flash.message = message
}

window.laravelEcho
    .channel('onecrm.phpeol.batch')
    .listen(
        '.phpeol.batch_created',
        ({ message }) => {
            bulkCreationDone(message)
        }
    )
    .listen(
        '.phpeol.batch_create_failed',
        ({ error }) => {
            bulkCreationFailed(error)
        }
    )
    .listen(
        '.phpeol.json_successfully_downloaded',
        ({ message, closeModal }) => {
            jsonDownloaded(message, closeModal)
        }
    )
    .listen(
        '.phpeol.json_download_failed',
        ({ error }) => {
            jsonDownloadFailed(error)
        }
    )
    .listen(
        '.phpeol.json_download_estimated_time',
        ({ message }) => {
            showEstimatedDownloadTime(message)
        }
    )

</script>

<style lang="scss" scoped>
.header-actions {
    display: grid;
    justify-items: flex-start;
    gap: 10px;
}
</style>
